import type { AgentPresenceStatus, AgentWithPresence } from './AgentPresenceTypes.ts';

export const AGENT_PRESENCE_INACTIVE_THRESHOLD_MS: number = 30 * 1000;
export const AGENT_PRESENCE_EXPIRY_THRESHOLD_MS: number =
	AGENT_PRESENCE_INACTIVE_THRESHOLD_MS + 5 * 60 * 1000;

const hasValidLastActive = (lastActive: number | undefined): lastActive is number =>
	typeof lastActive === 'number' && Number.isFinite(lastActive);

export const getAgentPresenceStatus = (
	agent: Pick<AgentWithPresence, 'lastActive'>,
	now: number = Date.now(),
): AgentPresenceStatus => {
	if (!hasValidLastActive(agent.lastActive)) {
		return 'active';
	}

	const age = now - agent.lastActive;
	if (age >= AGENT_PRESENCE_EXPIRY_THRESHOLD_MS) {
		return 'expired';
	}
	if (age >= AGENT_PRESENCE_INACTIVE_THRESHOLD_MS) {
		return 'inactive';
	}
	return 'active';
};

export const getNextAgentPresenceBoundary = (
	lastActive: number | undefined,
	now: number = Date.now(),
): number | undefined => {
	if (!hasValidLastActive(lastActive)) {
		return undefined;
	}

	const inactiveAt = lastActive + AGENT_PRESENCE_INACTIVE_THRESHOLD_MS;
	if (inactiveAt > now) {
		return inactiveAt;
	}

	const expiresAt = lastActive + AGENT_PRESENCE_EXPIRY_THRESHOLD_MS;
	return expiresAt > now ? expiresAt : undefined;
};

export const isAgentPresenceInactive = (agent: AgentWithPresence): boolean =>
	getAgentPresenceStatus(agent) !== 'active';
